/**
 * Operations processor for advanced URL pattern transformations
 * Supports: array, lowercase, uppercase, capitalize, map, regex, default operations
 */

// Type definitions for operations
export interface AdvancedConfig {
  fields: {
    [fieldPath: string]: {
      operations: Operation[];
    };
  };
}

export interface Operation {
  type: 'array' | 'lowercase' | 'uppercase' | 'capitalize' | 'map' | 'regex' | 'default';
  config?: OperationConfig;
}

export type OperationConfig = ArrayConfig | MapConfig | RegexConfig | DefaultConfig;

export interface ArrayConfig {
  pick?: 'all' | 'first' | 'last';
  joinWith?: string;
}

export interface MapConfig {
  [key: string]: string; // Mapping dictionary
}

export interface RegexConfig {
  pattern: string;
  replace: string;
}

export interface DefaultConfig {
  value: any;
}

/**
 * Parse advanced_config JSON string safely
 */
export function parseAdvancedConfig(advancedConfigJson?: string): AdvancedConfig | null {
  if (!advancedConfigJson || typeof advancedConfigJson !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(advancedConfigJson);
    // Validate structure
    if (parsed && typeof parsed === 'object' && parsed.fields) {
      return parsed as AdvancedConfig;
    }
    return null;
  } catch (error) {
    console.error('Failed to parse advanced_config JSON:', error);
    return null;
  }
}

/**
 * Apply array operation to a value
 */
export function applyArrayOperation(value: any, config?: ArrayConfig): string {
  if (!Array.isArray(value)) {
    // If not an array, convert to string
    return String(value || '');
  }

  if (value.length === 0) {
    return '';
  }

  const pick = config?.pick || 'all';
  const joinWith = config?.joinWith || ',';

  let selectedValues: any[];

  switch (pick) {
    case 'first':
      selectedValues = [value[0]];
      break;
    case 'last':
      selectedValues = [value[value.length - 1]];
      break;
    case 'all':
    default:
      selectedValues = value;
      break;
  }

  return selectedValues.map(v => String(v)).join(joinWith);
}

/**
 * Apply lowercase operation
 */
export function applyLowercaseOperation(value: any): string {
  return String(value || '').toLowerCase();
}

/**
 * Apply uppercase operation
 */
export function applyUppercaseOperation(value: any): string {
  return String(value || '').toUpperCase();
}

/**
 * Apply capitalize operation (first letter uppercase)
 */
export function applyCapitalizeOperation(value: any): string {
  const str = String(value || '');
  if (str.length === 0) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Apply map operation (value mapping)
 */
export function applyMapOperation(value: any, config?: MapConfig): string {
  if (!config) {
    return String(value || '');
  }

  const stringValue = String(value || '');
  
  // Check if mapping exists for this value
  if (config.hasOwnProperty(stringValue)) {
    return config[stringValue];
  }

  // No mapping found, return original value
  return stringValue;
}

/**
 * Apply regex operation (pattern matching and replacement)
 */
export function applyRegexOperation(value: any, config?: RegexConfig): string {
  if (!config || !config.pattern) {
    return String(value || '');
  }

  try {
    const stringValue = String(value || '');
    const regex = new RegExp(config.pattern, 'g');
    const replacement = config.replace || '';
    return stringValue.replace(regex, replacement);
  } catch (error) {
    console.error('Invalid regex pattern:', config.pattern, error);
    return String(value || '');
  }
}

/**
 * Apply default operation (fallback value)
 */
export function applyDefaultOperation(value: any, config?: DefaultConfig): any {
  // If value is null, undefined, or empty string, use default
  if (value === null || value === undefined || value === '') {
    return config?.value !== undefined ? config.value : '';
  }
  return value;
}

/**
 * Apply operations pipeline to a value
 * Operations are applied in sequence
 */
export function applyOperations(
  value: any,
  operations: Operation[] | undefined,
  context: Record<string, any> = {}
): string {
  if (!operations || operations.length === 0) {
    return String(value || '');
  }

  let currentValue: any = value;

  for (const operation of operations) {
    switch (operation.type) {
      case 'array':
        currentValue = applyArrayOperation(currentValue, operation.config as ArrayConfig);
        break;
      case 'lowercase':
        currentValue = applyLowercaseOperation(currentValue);
        break;
      case 'uppercase':
        currentValue = applyUppercaseOperation(currentValue);
        break;
      case 'capitalize':
        currentValue = applyCapitalizeOperation(currentValue);
        break;
      case 'map':
        currentValue = applyMapOperation(currentValue, operation.config as MapConfig);
        break;
      case 'regex':
        currentValue = applyRegexOperation(currentValue, operation.config as RegexConfig);
        break;
      case 'default':
        currentValue = applyDefaultOperation(currentValue, operation.config as DefaultConfig);
        break;
      default:
        console.warn(`Unknown operation type: ${(operation as any).type}`);
        break;
    }
  }

  return String(currentValue || '');
}

